import { useState } from "react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";

// Import gallery images
import cctvImg from "@/assets/gallery/cctv-installation.jpg";
import accessImg from "@/assets/gallery/access-control.jpg";
import networkImg from "@/assets/gallery/network-cabling.jpg";
import attendanceImg from "@/assets/gallery/attendance-system.jpg";
import alarmImg from "@/assets/gallery/alarm-system.jpg";
import technicianImg from "@/assets/gallery/technician-work.jpg";

const galleryItems = [
  {
    src: cctvImg,
    alt: "Sistema de videovigilancia CCTV instalado",
    title: "Videovigilancia",
    description: "Cámaras de seguridad en edificio comercial",
  },
  {
    src: accessImg,
    alt: "Control de acceso biométrico",
    title: "Control de Acceso",
    description: "Lector biométrico con tarjeta de proximidad",
  },
  {
    src: networkImg,
    alt: "Cableado estructurado de red",
    title: "Redes",
    description: "Infraestructura de cableado organizado",
  },
  {
    src: attendanceImg,
    alt: "Terminal de control de asistencia",
    title: "Control de Asistencia",
    description: "Terminal con reconocimiento facial",
  },
  {
    src: alarmImg,
    alt: "Panel de alarma de seguridad",
    title: "Alarmas",
    description: "Sistema de alarma con panel de control",
  },
  {
    src: technicianImg,
    alt: "Técnicos instalando equipos",
    title: "Nuestro Equipo",
    description: "Instalación profesional en sitio",
  },
];

const NewGallery = () => {
  const headerAnimation = useScrollAnimation({ threshold: 0.1 });
  const galleryAnimation = useScrollAnimation({ threshold: 0.05 });
  const [selectedImage, setSelectedImage] = useState<typeof galleryItems[0] | null>(null);

  return (
    <section className="py-14 md:py-20 bg-muted/30" id="proyectos">
      <div className="container px-4 sm:px-6">
        {/* Header */}
        <div
          ref={headerAnimation.ref as React.RefObject<HTMLDivElement>}
          className={cn(
            "text-center mb-10 sm:mb-12 transition-all duration-700",
            headerAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <span className="inline-block text-primary font-medium mb-3 uppercase tracking-wide text-sm">
            Proyectos realizados
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
            Instalaciones que hablan por sí solas
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">
            Cada proyecto refleja nuestro compromiso con la calidad y el profesionalismo.
          </p>
        </div>

        {/* Gallery Grid */}
        <div
          ref={galleryAnimation.ref as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
        >
          {galleryItems.map((item, index) => (
            <div
              key={index}
              className={cn(
                "group relative overflow-hidden rounded-xl cursor-pointer transition-all duration-500",
                galleryAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: galleryAnimation.isVisible ? `${index * 100}ms` : "0ms" }}
              onClick={() => setSelectedImage(item)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-tera-dark/90 via-tera-dark/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <h3 className="text-primary-foreground font-semibold text-lg">
                  {item.title}
                </h3>
                <p className="text-primary-foreground/80 text-sm">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Instagram CTA */}
        <div className="mt-8 sm:mt-10 flex justify-center px-2">
          <a
            href="https://www.instagram.com/teraseguridad"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-col sm:flex-row items-center gap-1 sm:gap-3 px-4 sm:px-6 py-3 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] rounded-xl hover:opacity-90 hover:scale-[1.02] transition-all duration-300 shadow-lg text-center"
          >
            <span className="text-white text-sm sm:text-base font-medium">¿Quieres ver más proyectos?</span>
            <span className="text-white/90 text-sm sm:text-base font-semibold">Síguenos en Instagram</span>
          </a>
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/80 hover:bg-background flex items-center justify-center transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          
          {selectedImage && (
            <div className="relative rounded-xl overflow-hidden">
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-tera-dark/90 to-transparent p-6">
                <h3 className="text-primary-foreground font-semibold text-xl mb-1">
                  {selectedImage.title}
                </h3>
                <p className="text-primary-foreground/80">
                  {selectedImage.description}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default NewGallery;
